import React from "react";
import { Descriptions, Divider, Drawer, Table } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import moment from "moment";
import { numberWithCommas } from "../../../utils";

function ProductionDataView({ onClose, open, prodd }) {
  const url = "api/v1/production-varieties/list";

  const { dataVarieties } = useSelector((state) => ({
    dataVarieties: state.fetch.data[url] || [],
  }));

  // Arrays of {id, name} render as their labels
  const names = (list) =>
    list?.length > 0 ? list?.map((item) => item?.name)?.join(", ") : "N/A";

  const num = (value) =>
    value || value === 0 ? numberWithCommas(value) : "N/A";

  const schemeItems = [
    { key: "1", label: "Scheme", children: prodd?.scheme?.name ?? "N/A" },
    { key: "2", label: "County", children: prodd?.countyName ?? "N/A" },
    { key: "3", label: "Financial Year", children: prodd?.financialYear ?? "N/A" },
    { key: "4", label: "Crop", children: prodd?.crop?.name ?? "N/A" },
    {
      key: "5",
      label: "Potential Acreage (Acres)",
      children: num(prodd?.potentialAcreage),
    },
    {
      key: "6",
      label: "Total Command Area (Acres)",
      children: num(prodd?.commandArea),
    },
    {
      key: "7",
      label: "Developed Area (Acres)",
      children: num(prodd?.developedArea),
    },
    {
      key: "8",
      label: "Irrigated Area (Acres)",
      children: num(prodd?.irrigatedArea),
    },
    {
      key: "9",
      label: "No. of Farmers",
      children: num(prodd?.farmersNumber),
    },
  ];

  const productionItems = [
    {
      key: "1",
      label: "Total Area Under Production (Acres)",
      children: num(prodd?.cropAcreage),
    },
    { key: "2", label: "Varieties", children: names(prodd?.varieties) },
    {
      key: "3",
      label: "Total Production (MT)",
      children: num(prodd?.totalProduction),
    },
    {
      key: "4",
      label: "Production per Acre (MT/Acre)",
      children:
        prodd?.productionPerAcre || prodd?.productionPerAcre === 0
          ? Number(prodd?.productionPerAcre).toFixed(2)
          : "N/A",
    },
    { key: "5", label: "Seasons", children: prodd?.seasonCount ?? "N/A" },
    {
      key: "6",
      label: "Ongoing Interventions",
      children: prodd?.ongoingInterventions ?? "N/A",
    },
  ];

  const marketItems = [
    {
      key: "1",
      label: "Distance to Agroinputs",
      children: prodd?.agrochemicalAccess ?? "N/A",
    },
    {
      key: "2",
      label: "Aggregation Points / Warehouses",
      children: prodd?.aggregationPoints ?? "N/A",
    },
    {
      key: "3",
      label: "Milling Facilities Available",
      children: prodd?.millingAvailable ?? "N/A",
    },
    {
      key: "4",
      label: "Milling Capacity (Tonnage per hour)",
      children: num(prodd?.millingCapacity),
    },
    {
      key: "5",
      label: "Milling Capacity Sufficient",
      children: prodd?.millingSufficient ?? "N/A",
    },
    {
      key: "6",
      label: "Sufficiency Description",
      children: prodd?.millingSufficientDesc ?? "N/A",
    },
    {
      key: "7",
      label: "Market Channels",
      children: names(prodd?.marketChannels),
    },
  ];

  const infraItems = [
    {
      key: "1",
      label: "Infrastructure Issues",
      children: prodd?.infrastructureIssues ?? "N/A",
    },
    {
      key: "2",
      label: "Issues Description",
      children: prodd?.infrastructureIssuesDesc ?? "N/A",
    },
    {
      key: "3",
      label: "Operational Status",
      children: prodd?.operationalStatus?.name ?? "N/A",
    },
    {
      key: "4",
      label: "Operation and Maintenance Cost (KES)",
      children: num(prodd?.omCost),
    },
    {
      key: "5",
      label: "Level of Mechanization",
      children: prodd?.mechanizationLevel ?? "N/A",
    },
  ];

  const servicesItems = [
    {
      key: "1",
      label: "Pests and Diseases Control",
      children: names(prodd?.pestsControlled),
    },
    {
      key: "2",
      label: "Extension Services Available",
      children: names(prodd?.extensionServices),
    },
    {
      key: "3",
      label: "Post Harvest Loss (%)",
      children:
        prodd?.postHarvestLoss || prodd?.postHarvestLoss === 0
          ? `${prodd?.postHarvestLoss}%`
          : "N/A",
    },
    {
      key: "4",
      label: "How the Loss Occurs",
      children: prodd?.postHarvestLossCause ?? "N/A",
    },
    {
      key: "5",
      label: "Value Addition Providers Available",
      children: prodd?.valueAdditionAvailable ?? "N/A",
    },
    {
      key: "6",
      label: "Type of Value Addition",
      children: names(prodd?.valueAdditionCapacity),
    },
  ];

  const challengeItems = [
    {
      key: "1",
      label: "Type of Mechanization And Technology",
      children: names(prodd?.mechanizationTypes),
    },
    {
      key: "2",
      label: "Mechanization Narrative",
      children: prodd?.mechanizationNarrative ?? "N/A",
    },
    {
      key: "3",
      label: "Farmers Trainings",
      children: prodd?.farmerTrainings ?? "N/A",
    },
    {
      key: "4",
      label: "Efficiency of the Irrigation System",
      children: prodd?.irrigationEfficiency ?? "N/A",
    },
    {
      key: "5",
      label: "Improved Seed Varieties",
      children: prodd?.improvedSeedVarieties ?? "N/A",
    },
    {
      key: "6",
      label: "Invasive Species",
      children: prodd?.invasiveSpecies ?? "N/A",
    },
    {
      key: "7",
      label: "Climate Change Issues",
      children: prodd?.climateChangeIssues ?? "N/A",
    },
    {
      key: "8",
      label: "Recommendations",
      children: prodd?.recommendations ?? "N/A",
    },
  ];

  const varietyColumns = [
    {
      title: "Variety",
      dataIndex: ["variety", "name"],
      render: (item) => item ?? "--",
    },
    {
      title: "Season",
      dataIndex: ["season", "name"],
      render: (item) => item ?? "--",
    },
    {
      title: "Acreage",
      dataIndex: "acreage",
      align: "right",
      render: (item) => numberWithCommas(item ?? 0),
    },
    {
      title: "Production (Tonnes)",
      dataIndex: "production",
      align: "right",
      render: (item) => numberWithCommas(item ?? 0),
    },
    {
      title: "Productivity (Kg/Acre)",
      dataIndex: "averageProductionPerAcre",
      align: "right",
      render: (item) => numberWithCommas(item ?? 0),
    },
    {
      title: "Farm Gate Price (KES/Kg)",
      dataIndex: "farmGatePrice",
      align: "right",
      render: (item) => numberWithCommas(item ?? 0),
    },
    {
      title: "Revenue per Acre (KES)",
      dataIndex: "revenuePerAcre",
      align: "right",
      render: (item) => numberWithCommas(item ?? 0),
    },
    {
      title: "Cost per Acre (KES)",
      dataIndex: "costPerAcre",
      align: "right",
      render: (item) => numberWithCommas(item ?? 0),
    },
    {
      title: "Water Requirement (m3/s)",
      dataIndex: "waterRequirement",
      align: "right",
      render: (item) => item ?? "--",
    },
    {
      title: "Available Flow (m3/s)",
      dataIndex: "availableFlow",
      align: "right",
      render: (item) => item ?? "--",
    },
  ];

  const section = (title, items) => (
    <>
      <div className="custom-title !text-[#111928] rounded-[.5rem] mt-[1.75rem]">
        {title}
      </div>
      <Descriptions
        title=""
        column={2}
        colon={false}
        items={items}
        layout="vertical"
      />
    </>
  );

  return (
    <Drawer
      width={1100}
      title="Production Data"
      open={open}
      onClose={onClose}
      closeIcon={false}
      extra={
        <button type="button" onClick={onClose}>
          <CloseOutlined />
        </button>
      }
    >
      <div className="flex justify-between items-center">
        <div className="details_h">
          {prodd?.scheme?.name ?? "Scheme"} · {prodd?.financialYear ?? "--"} ·{" "}
          {prodd?.crop?.name ?? "--"}
        </div>
        <div className="proj_desc">
          {prodd?.createdBy ? `Captured by ${prodd?.createdBy}` : ""}
          {prodd?.createdDate
            ? ` on ${moment(prodd?.createdDate).format("DD-MM-YYYY")}`
            : ""}
        </div>
      </div>

      <div className="mt-[1rem]">
        <Divider />
      </div>

      {section("Scheme Details", schemeItems)}
      {section("Production", productionItems)}

      <div className="custom-title !text-[#111928] rounded-[.5rem] mt-[1.75rem]">
        Varieties
      </div>
      <div className="max-w-full w-full overflow-x-auto">
        <Table
          rowSelection={false}
          className="w-full"
          scroll={{
            x: "max-content",
          }}
          rowKey={(record) => record?.id}
          columns={varietyColumns}
          dataSource={dataVarieties}
          pagination={false}
        />
      </div>

      {section("Inputs, Aggregation and Markets", marketItems)}
      {section("Infrastructure", infraItems)}
      {section("Services and Losses", servicesItems)}
      {section("Challenges, Opportunities and Recommendations", challengeItems)}

      <div className="mt-[1.75rem]">
        <Divider />
        <div className="flex justify-end">
          <div className="w-[150px]">
            <button type="button" onClick={onClose} className="cstm-btn">
              Close
            </button>
          </div>
        </div>
      </div>
    </Drawer>
  );
}

export default ProductionDataView;
